import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from './base.service';
import departmentsData from '../../../assets/utils/datas/departement.json';
import { Language, ServiceAvailable } from '../interfaces/doctor.interface';
import { CountryStorageService } from './country.storage.service';
import { environment } from '../../../environments';

// Interfaces pour typer les données
interface Service {
  _id: string;
  createdBy: string;
  creationDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
  disable: boolean;
  documentGenerationRequired: boolean;
  name: string;
  description: string | null;
  id: number;
  eyoneExternalId: string | null;
  eyoneInternalId: string | null;
  globalInfo: any | null;
  code: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface AppointmentReason {
  _id: string;
  createdBy: string;
  creationDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
  disable: boolean;
  documentGenerationRequired: boolean;
  label: string;
  id: number;
  eyoneExternalId: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Country {
  id: number;
  code: string | null;
  name: string;
  flag?: string;
}

interface Insurer {
  name: string;
  id: number;
  countryId: number;
  country: Country;
  convention: any;
}

interface InsurersResponse {
  results: Insurer[];
  pagination: {
    totalItems: number;
    pageCount: number;
    currentPage: number;
    pageSize: number;
  };
}

// Interface pour les paramètres de recherche de médecins
interface DoctorSearchParams {
  // Pagination
  limit?: number;
  page?: number;

  // Filtres simples
  query?: string;
  doctorTypeId?: number;
  doctorTitleId?: number;

  // Filtres par collection (multiples)
  townId?: number;
  countryId?: number;
  countryCode?: string;
  languageIds?: number[] | string;
  insurerIds?: number[] | string;
  paymentTypeIds?: number[] | string;
  structureIds?: number[] | string;
  specialisationIds?: number[] | string;
  serviceAvailableIds?: number[] | string;

  insurerEyoneExternalId?: string;

  // Recherche géographique
  latitude?: number;
  longitude?: number;
  maxDistance?: number;
  includeDistance?: boolean;
}

// Interfaces pour les données locales
interface Department {
  id: number;
  name: string;
  region: string;
  communes: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ReferentielService extends BaseService {
  private departments: Department[] = [];

  constructor(
    protected override http: HttpClient,
    private countryStorage: CountryStorageService
  ) {
    super(http);
    this.loadDepartments();
  }

  searchAutocomplete(query: any): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/referentiel/search`, {
      params: this.createParams(query),
    });
  }

  searchInsurer(externalId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/referentiel/public/insurers/external/${externalId}`
    );
  }

  private loadDepartments(): void {
    try {
      this.departments = departmentsData;
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
      this.departments = [];
    }
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<any[]>(`${this.baseUrl}/referentiel/public/languages`).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response.map((lang) => ({
            id: lang.id,
            label: lang.label,
          }));
        }
        return [];
      })
    );
  }

  getServices(): Observable<ServiceAvailable[]> {
    return this.http
      .get<any[]>(`${this.baseUrl}/referentiel/public/available_services`)
      .pipe(
        map((response) => {
          if (Array.isArray(response)) {
            return response.map((service) => ({
              id: service.id,
              name: service.name,
            }));
          }
          return [];
        })
      );
  }

  getInsurers(countryCode?: string): Observable<Insurer[]> {
    const params: any = {};
    if (countryCode) params.countryCode = countryCode;
    return this.http
      .get<any>(`${this.baseUrl}/referentiel/public/insurers`, { params })
      .pipe(
        map((response) => {
          if (response && response.results && Array.isArray(response.results)) {
            return response.results.map((insurer: Insurer) => ({
              id: insurer.id,
              name: insurer.name,
            }));
          }
          return [];
        })
      );
  }

  getCountries() { //Observable<Country[]> {
    return this.http.get<any>(`${this.baseUrl}/referentiel/public/countries`).pipe(
      map((response) => {
        if (Array.isArray(response.results)) {
          return response.results.map((country: any) => ({
            id: country?.id,
            code: country?.code,
            name: country?.name,
            flag: country?.flag, // if your API sends emoji or image url
          }));
        }
        return [];
      })
    );
  }

  getAppointmentReasons(): Observable<AppointmentReason[]> {
    console.log('getAppointmentReasons called');
    return this.http
      .get<AppointmentReason[]>(
        `${this.baseUrl}/referentiel/public/appointment_reasons`
      )
      .pipe(
        map((response) => {
          console.log('Appointment reasons response:', response);
          if (Array.isArray(response)) {
            return response;
          }
          return [];
        })
      );
  }

  getRegions(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/referentiel/public/regions`);
  }

  /**
   * Récupère les départements d'une région donnée
   * @param regionName Nom de la région
   * @returns Observable des départements de la région
   */
  getDepartmentsByRegion(regionName: string): Observable<Department[]> {
    const filteredDepartments = this.departments.filter(
      (dept) => dept.region === regionName
    );
    return of(filteredDepartments);
  }

  /**
   * Récupère les communes d'un département donné
   * @param departmentName Nom du département
   * @returns Observable des communes du département
   */
  getCommunesByDepartment(departmentName: string): Observable<string[]> {
    const department = this.departments.find(
      (dept) => dept.name === departmentName
    );
    return of(department ? department.communes : []);
  }

  getDoctors(queryParams: DoctorSearchParams): Observable<any> {
    // Construire les paramètres de requête
    const params = this.buildDoctorParams(queryParams);

    return this.http.get<any>(`${this.baseUrl}/referentiel/public/doctors`, {
      params,
    });
  }

  /**
   * Récupère les pharmacies avec les mêmes paramètres que getDoctors
   * @param queryParams paramètres de recherche
   */
  getPharmacies(queryParams: DoctorSearchParams): Observable<any> {
    const params = this.buildDoctorParams(queryParams);

    return this.http.get<any>(`${this.baseUrl}/referentiel/public/pharmacies`, {
      params,
    });
  }

  /**
   * Récupère les structures médicales avec les mêmes paramètres que getDoctors
   * @param queryParams paramètres de recherche
   */
  getMedicalStructures(queryParams: DoctorSearchParams): Observable<any> {
    const params = this.buildDoctorParams(queryParams);

    return this.http.get<any>(
      `${this.baseUrl}/referentiel/public/medical_structures`,
      {
        params,
      }
    );
  }

  private buildDoctorParams(queryParams: DoctorSearchParams): HttpParams {
    const params: any = {};

    // Pagination
    if (queryParams.limit) {
      params.limit = queryParams.limit;
    }
    if (queryParams.page) {
      params.page = queryParams.page;
    }

    // Filtres simples
    if (queryParams.query) {
      params.query = queryParams.query;
    }
    if (queryParams.doctorTypeId) {
      params.doctorTypeId = queryParams.doctorTypeId;
    }
    if (queryParams.doctorTitleId) {
      params.doctorTitleId = queryParams.doctorTitleId;
    }

    // Filtres par collection (multiples)
    if (queryParams.townId) {
      params.townId = queryParams.townId;
    }
    if (queryParams.countryId) {
      params.countryId = queryParams.countryId;
    }
    if (queryParams.countryCode) {
      params.countryCode = queryParams.countryCode;
    }
    if (queryParams.languageIds) {
      params.languageIds = Array.isArray(queryParams.languageIds)
        ? queryParams.languageIds.join(',')
        : queryParams.languageIds;
    }
    if (queryParams.insurerIds) {
      params.insurerIds = Array.isArray(queryParams.insurerIds)
        ? queryParams.insurerIds.join(',')
        : queryParams.insurerIds;
    }
    if (queryParams.insurerEyoneExternalId) {
      params.insurerEyoneExternalId = queryParams.insurerEyoneExternalId;
    }
    if (queryParams.paymentTypeIds) {
      params.paymentTypeIds = Array.isArray(queryParams.paymentTypeIds)
        ? queryParams.paymentTypeIds.join(',')
        : queryParams.paymentTypeIds;
    }
    if (queryParams.structureIds) {
      params.structureIds = Array.isArray(queryParams.structureIds)
        ? queryParams.structureIds.join(',')
        : queryParams.structureIds;
    }
    if (queryParams.specialisationIds) {
      params.specialisationIds = Array.isArray(queryParams.specialisationIds)
        ? queryParams.specialisationIds.join(',')
        : queryParams.specialisationIds;
    }
    if (queryParams.serviceAvailableIds) {
      params.serviceAvailableIds = Array.isArray(
        queryParams.serviceAvailableIds
      )
        ? queryParams.serviceAvailableIds.join(',')
        : queryParams.serviceAvailableIds;
    }

    // Recherche géographique
    if (queryParams.latitude) {
      params.latitude = queryParams.latitude;
    }
    if (queryParams.longitude) {
      params.longitude = queryParams.longitude;
    }
    if (queryParams.maxDistance) {
      params.maxDistance = queryParams.maxDistance;
    }
    if (queryParams.includeDistance !== undefined) {
      params.includeDistance = queryParams.includeDistance;
    }

    // Utiliser la méthode createParams du BaseService
    return this.createParams(params);
  }

  getDoctorDetails(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/referentiel/public/doctors/${id}`
    );
  }

  getStructureDetails(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/referentiel/public/medical_structures/${id}`
    );
  }

  getPharmacyDetails(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/referentiel/public/pharmacies/${id}`
    );
  }

  /**
   * Recherche de médecins avec spécialités
   * @param search Termes de recherche
   * @param page Numéro de page pour la pagination
   * @param limit Nombre d'éléments par page
   * @return Observable des résultats de recherche
   */
  searchDoctorsWithSpecialities(
    search: string,
    page = 1,
    limit = 10
  ): Observable<any> {
    const countryCode = this.countryStorage.currentCountryCode?.toUpperCase();

    const params = this.createParams({
      search,
      page,
      limit,
      countryCode: countryCode,
    });

    return this.http
      .get<any>(`${this.baseUrl}/referentiel/search/with-doctors-specialities`, {
        params,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error fetching doctors with specialities:', error);
          return of([]);
        })
      );
  }

  /**
   * Recherche d'autocomplétion pour les villes
   * @param search Termes de recherche
   * @return Observable des résultats d'autocomplétion
   */
  townSearchAutocomplete(search: string): Observable<any> {
    const countryCode = this.countryStorage.currentCountryCode?.toUpperCase();

    const params = this.createParams({
      countryCode: countryCode,
      q: search,
    });

    return this.http
      .get<any>(`${this.baseUrl}/referentiel/search/autocomplete/town`, {
        params,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error fetching town autocomplete:', error);
          return of([]);
        })
      );
  }

  getTownById(id: string | number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/referentiel/public/towns/${id}`).pipe(
      catchError((error) => {
        console.error('Error fetching town by id:', error);
        return of(null);
      })
    );
  }

  getDoctorSpecializationById(id: string | number): Observable<any> {
    return this.http
      .get<any>(`${this.baseUrl}/referentiel/public/doctor_specializations/${id}`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching doctor specialization by id:', error);
          return of(null);
        })
      );
  }
}
